// @atlas/contracts — a `messages` modul Zod-sémái
//
// A fájl CSAK sémákat tart (érték-szintű Zod-objektumok + a belőlük inferált típusok) — a ROUTE-ok
// egyetlen `atlasContract` objektum-literálban maradnak (`src/index.ts`), mert a ts-rest
// kontextus-inferenciája a lapos bővítésre érzékeny. A sémák mozgatása ezt NEM érinti: sima értékek.
import { z } from 'zod';
import { PersonOptionSchema } from '../_shared/schemas.js';
/**
 * Üzenet-melléklet sor (belső nézet): a `communication.message-attachments` [T] busz-sor fehérlistás mezői
 * (`messageId`/`filename`/`fileId`) + a BFF-oldali dúsítás: friss presigned GET-URL (`downloadUrl`) és a
 * feltöltő neve (`uploadedByName`). A bájtok SOHA nem a BFF-en át. A `legacyId` sosem jön.
 */
export const MessageAttachmentRowSchema = z.object({
    id: z.string(),
    messageId: z.string(),
    filename: z.string(),
    /** A `platform.file_metadata` sor id-je; a LEGACY-ből örökölt sornál NULL (a bájtok nem jöttek át). */
    fileId: z.string().nullable(),
    /** Friss presigned GET-URL (rövid TTL); NULL, ha nincs `fileId` vagy nem oldható fel. */
    downloadUrl: z.string().nullable(),
    uploadedById: z.string().nullable(),
    uploadedByName: z.string().nullable().optional(),
    createdAt: z.string().nullable(),
    version: z.number(),
});
/** Presigned feltöltő-URL kérése (üzenet) — a mezők és a szabályok a ticket-csatolmánnyal AZONOSAK. */
export const MessageAttachmentUploadUrlBodySchema = z.object({
    filename: z.string().trim().min(1).max(255),
    contentType: z.string().trim().min(1).max(255),
    sizeBytes: z.number().int().positive(),
});
/** Presigned feltöltő-URL válasz (üzenet): PUT-URL + a létrejött `file_metadata` id + a tár-kulcs. */
export const MessageAttachmentUploadUrlResponseSchema = z.object({
    uploadUrl: z.string(),
    fileId: z.string(),
    key: z.string(),
});
/** Commit body (üzenet) — a tár-PUT UTÁN: a `fileId` + a végleges `filename`. A `fileId` plain-UUID (traversal). */
export const MessageAttachmentCommitBodySchema = z.object({
    fileId: z.string().min(1),
    filename: z.string().trim().min(1).max(255),
});
/**
 * Egy NÉVVEL feloldott üzenet-címzett a lista-cellában (a legacy címzett-modal 1:1, avatar + név). CSAK az
 * identity-alapú dimenziók (dolgozó/kontakt) oldódnak fel névvel — a `kind` ezekből (`employee` | `contact`),
 * a `name` a foundation `identities`-ből (a küldő-névvel KÖZÖS, már betöltött identity-mapből; extra
 * upstream nélkül). A NEM identity-alapú dimenziók (fiók/szolgáltatás/részleg) kereszt-namespace-ek → azok
 * NÉV nélkül, darabszám-összegzésben mennek (`recipientGroups`); lásd a controller modul-kommentjét.
 */
export const MessageRecipientSchema = z.object({
    /** A címzett identity-je (platform.identities) — avatar-monogram + a lista React-kulcsa. */
    id: z.string(),
    /**
     * Feloldott megjelenítendő név (identities displayName, fallback e-mail); `null`, ha az identity nem
     * oldható fel (törölt személy) — a felület „Törölt felhasználó"-t mutat, nem a nyers UUID-t.
     */
    name: z.string().nullable(),
    /** Strukturált név-részek a NYELV-TUDATOS névsorrendhez (hu: vezetéknév elöl) — a formázás kliens-oldali. */
    givenName: z.string().nullable(),
    familyName: z.string().nullable(),
    /** Presigned avatar-URL (null → monogram-fallback) — a cella avatar + név alakban jelenít. */
    avatarUrl: z.string().nullable(),
    /** A kolléga csapat-színe (az avatar háttere, mint minden más listán); külsősnél és csapat nélkül `null`. */
    teamColor: z.string().nullable(),
    /** `employee` | `contact` — a legacy recipient-dimenzió (a pivot-forrásból). */
    kind: z.string(),
});
/**
 * Egy NÉV nélküli címzett-csoport összegzése (fiók/szolgáltatás/csapat/részleg): a legacy
 * recipient-dimenzió + darabszám. A NEVEK feloldása kereszt-namespace-t (platform.accounts /
 * billing.services / workforce.teams / workforce.departments) igényelne → a v1 lista a nevet NEM oldja
 * fel (extra upstream nélkül — lásd a controller kommentjét), csak a dimenzió-típust és a darabszámot mutatja.
 */
export const MessageRecipientGroupSchema = z.object({
    /** `account` | `service` | `team` | `department` — a kereszt-namespace címzett-dimenzió. */
    kind: z.string(),
    /** Hány cél tartozik a csoportba (a pivot-sorok száma az üzenetre) — ez a MÉRVADÓ darabszám. */
    count: z.number(),
    /**
     * A feloldott célok NEVEI, ábécé-rendben és PLAFONOSAN (25) — megjelenítési dúsítás (tooltip + export), nem
     * a darabszám forrása. Ezért lehet rövidebb, mint a `count`: egy „minden fiók" címzés 300+ nevet jelentene,
     * és a lista-cellának nem az a dolga. Feloldhatatlan/upstream-hibás névnél ÜRES (a `count` akkor is helyes).
     */
    names: z.array(z.string()),
});
/**
 * Üzenet-sor (communication üzenőfal, BELSŐ nézet): a `communication.messages` [T] busz-erőforrás
 * fehérlistás mezői (type/status/subject/…) + a BFF-oldali dúsítás: a küldő identity-neve (`senderName`:
 * a busz-soron a `createdBy` UUID → foundation `identities` név-feloldás), a címzés (a legacy
 * címzett-mátrix 1:1): `audienceEveryone`/`audienceAllClients` flagek + a NÉVVEL feloldott identity-címzettek
 * (`recipients`: dolgozó/kontakt, a küldő-névvel közös identity-mapből) + a kereszt-namespace csoportok
 * darabszám-összegzése (`recipientGroups`: fiók/szolgáltatás/részleg — NÉV nélkül, lásd a controllert), az
 * OLVASÁS-szám (`readCount`: a `communication.message-reads` üzenetre aggregálva) és a MELLÉKLET-/LINK-szám
 * (`attachmentCount`: a `message-attachments` pivotból; `linkCount`: a soron lévő drive/meet/zoom linkekből).
 * A `body` HTML (TipTap) — a lista NEM rendeli meg, csak a kereséshez használjuk a BFF-ben; a szerződés a
 * törzset NEM hordozza (zaj-/PII-fegyelem). A `legacyId` sosem jön. Sor-részletnézet nincs.
 */
export const MessageRowSchema = z.object({
    id: z.string(),
    /** internal | external — MINDKETTŐ CRM-en belüli fal (NEM email); a Schema Registry enum 1:1. */
    type: z.string(),
    /** Olvasta-e MÁR az aktuális identity (a `message-reads`-ből) — az olvasatlan-jelölés + „mind olvasott" UX-hez. */
    readByMe: z.boolean(),
    /** OCC-verzió (a busz-sor `version`-je) — a sor-akciók (send/schedule/edit) `If-Match`-éhez. */
    version: z.number(),
    /** draft | scheduled | sent (a legacy failed/cancelled kivezetve). */
    status: z.string(),
    subject: z.string(),
    /** A küldő identity-nyers UUID-ja (a busz `createdBy`-ja); lehet null (törölt/ismeretlen létrehozó). */
    senderId: z.string().nullable(),
    /** A küldő ember-olvasható neve (foundation `identities` displayName, fallback e-mail); feloldva. */
    senderName: z.string().nullable(),
    /** A küldő név-részei + avatarja — a lista-cella avatar + nyelvhelyes név megjelenítéséhez. */
    senderGivenName: z.string().nullable(),
    senderFamilyName: z.string().nullable(),
    senderAvatarUrl: z.string().nullable(),
    /** A küldő csapat-színe (az avatar háttere) — külsősnél és csapat nélkül `null`. */
    senderTeamColor: z.string().nullable(),
    /**
     * KATEGÓRIA: `message` = ember írta (van belső/külső feladó identity) · `system` = rendszer-generált
     * (gépi/`service` identity, vagy nincs feladó). A felület ezt külön címkével és szűrővel jelöli — a
     * „Személyes üzenetek és rendszerértesítések" lap KÉT tartalom-fajtája. A besorolás SZERVER-oldali (a
     * feladó identity `type`-jából), hogy a kliens ne találgasson.
     */
    category: z.enum(['message', 'system']),
    /** ★ Címzés-flag: mindenkinek szólt (a legacy everyone=1). */
    audienceEveryone: z.boolean(),
    /** ★ Címzés-flag: minden ügyfélnek szólt (a legacy all_clients=1). */
    audienceAllClients: z.boolean(),
    /** NÉVVEL feloldott identity-címzettek (dolgozó/kontakt) — a legacy címzett-modal listája 1:1. */
    recipients: z.array(MessageRecipientSchema),
    /** NÉV nélküli címzett-csoportok darabszáma (fiók/szolgáltatás/részleg — kereszt-namespace). */
    recipientGroups: z.array(MessageRecipientGroupSchema),
    /** Hány identity olvasta az üzenetet (a message-reads üzenetre aggregálva) — a „X olvasás" numerátora. */
    readCount: z.number(),
    /** Melléklet-szám (a `communication.message-attachments` pivot üzenetre aggregálva). */
    attachmentCount: z.number(),
    /** Link-szám (a soron lévő google drive / google meet / zoom linkek közül a kitöltöttek). */
    linkCount: z.number(),
    /** Kiküldés dátuma (ISO datetime) — a busz `sentAt`-ja; ha üres (draft/scheduled), a `createdAt`. */
    sentAt: z.string().nullable(),
    /** Ütemezett kiküldés dátuma (ISO datetime) — csak `scheduled` státusznál releváns, egyébként null. */
    scheduledAt: z.string().nullable(),
    /** Rögzítve/létrehozva (ISO datetime) — a busz createdAt-ja. */
    createdAt: z.string().nullable(),
    /**
     * KITŰZVE (ISO datetime) — a kitűzött körüzenet a belső kezdőlapon jelenik meg (a legfrissebb kitűzött); null:
     * nincs kitűzve. Üzenet-szintű (minden olvasónál ugyanaz).
     */
    pinnedAt: z.string().nullable(),
    /**
     * ARCHIVÁLTA-e az aktuális identity (olvasó-oldali elrejtés — a saját listájából; a többieknél megmarad). Az
     * alap-nézet az archiváltakat kihagyja, az „Archivált" nézet csak ezeket mutatja.
     */
    archivedByMe: z.boolean(),
});
/**
 * Üzenet-lista lekérdezés: típus-szűrő (internal/external) + szabad-szöveg (tárgy/tartalom) + rendezés-irány
 * (a fix kiküldés-dátum kulcson, alap DESC — a legacy default) + offset-lapozás (végtelen görgetés).
 */
export const MessageListQuerySchema = z.object({
    q: z.string().optional(),
    /**
     * ÜZENET-IRÁNY (a legacy „Bejövő" / „Kimenő" fülei):
     *   • `incoming` — amit MÁS küldött, és már kiment (`sent`),
     *   • `outgoing` — amit ÉN küldtem (bármely státusz: piszkozat/időzített is),
     *   • elhagyva — mindkettő (az Atlas „Mind" füle).
     * A hívó identitása a TOKENBŐL jön (nem paraméter) — az irány nem hamisítható.
     */
    direction: z.enum(['incoming', 'outgoing']).optional(),
    type: z.string().optional(),
    /**
     * KATEGÓRIA-szűrő: `message` = ember írta · `system` = rendszer-generált (gépi/`service` feladó vagy nincs
     * feladó). A besorolás szerver-oldali (a feladó identity típusából) — a kliens csak választ.
     */
    category: z.enum(['message', 'system']).optional(),
    /** A kiküldés-dátum (sentAt|createdAt) rendezés iránya — a fejléc-kattintás váltja. Alap: `desc`. */
    order: z.enum(['asc', 'desc']).optional(),
    /**
     * OLVASÓ-OLDALI ARCHÍVUM: `only` → CSAK az általam archivált üzenetek (az „Archivált" nézet); elhagyva → az
     * archiváltak KIMARADNAK (az alap-nézet). A darabszámok (`directions`) ugyanígy számolnak.
     */
    archived: z.enum(['only']).optional(),
    /** KITŰZÖTT: `only` → csak a kitűzött üzenetek, a kitűzés ideje szerint csökkenőben (a kezdőlap-kártya forrása). */
    pinned: z.enum(['only']).optional(),
    limit: z.coerce.number().int().positive().max(200).optional(),
    offset: z.coerce.number().int().nonnegative().optional(),
});
/** Üres string → null (link-mező törlés) — a modulok közös „üres → null" szabálya. */
const messagesEmptyToNull = (v) => typeof v === 'string' && v.trim() === '' ? null : v;
/** Opcionális link-mező: generikus http(s) URL-formátum (mint a legacy), max 2083 (legacy oszlop-hossz). */
const MessageLinkSchema = z.preprocess(messagesEmptyToNull, z
    .string()
    .trim()
    .max(2083)
    .regex(/^https?:\/\/\S+$/, 'Érvényes http(s) URL-t adj meg.')
    .nullable()
    .optional());
/**
 * Üzenet-tartalom (compose + szerkesztés KÖZÖS 5 mezője): tárgy (≤255), törzs (HTML — a BFF szanitálja a
 * TipTap-allowlistre és a PLAIN-TEXT hosszra [tag-strip + whitespace-collapse után min. 25] a BFF ad 422-t,
 * mert a markup-padding a Zod-hosszon átcsúszna), 3 opcionális link (http(s) URL). A címzés ITT NINCS —
 * a címzett utólag SOSEM szerkeszthető (legacy-parity; új célzás = új üzenet).
 */
export const MessageContentSchema = z.object({
    subject: z.string().trim().min(1).max(255),
    /** HTML (TipTap); a plain-text min. 25 hossz + allowlist-szanitálás a BFF-en (422 mező-hibával). */
    body: z.string().min(1).max(100_000),
    googleDriveLink: MessageLinkSchema,
    googleMeetLink: MessageLinkSchema,
    zoomLink: MessageLinkSchema,
});
/** Célzott id-lista (címzett-dimenzió): min. 1 (üres célzás → 422 — nincs implicit „mindenki"), max. 500. */
const messageIdArr = z.array(z.string().uuid()).min(1).max(500);
/**
 * BELSŐ címzés — EXPLICIT rádió-választás (a legacy „üres = mindenki" implicit default NEM portolódik):
 * mindenki | részleg(ek) | csapat(ok) | kolléga/kollégák. A discriminated union kizárja a keresztbe-célzást.
 *
 * KÉT SZERVEZETI SZEMCSÉZET: a `teams` a finom (közvetlenül a csapat tagjai), a `departments` a durva —
 * az utóbbi küldéskor a részleg CSAPATAIN keresztül bomlik ki tagokra (részleg → csapatok → beosztások →
 * munkatársak). Egy üzenet vagy az egyikkel, vagy a másikkal céloz, keverve nem.
 */
export const InternalAudienceSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('everyone') }),
    z.object({ kind: z.literal('departments'), ids: messageIdArr }),
    z.object({ kind: z.literal('teams'), ids: messageIdArr }),
    z.object({ kind: z.literal('colleagues'), ids: messageIdArr }),
]);
/** KÜLSŐ címzés — EXPLICIT: minden ügyfél | fiók(ok) | kapcsolattartó(k) | szolgáltatás(ok). */
export const ExternalAudienceSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('all_clients') }),
    z.object({ kind: z.literal('accounts'), ids: messageIdArr }),
    z.object({ kind: z.literal('contacts'), ids: messageIdArr }),
    z.object({ kind: z.literal('services'), ids: messageIdArr }),
]);
/**
 * Compose-body (discriminated union): a type↔audience kölcsönösen kizáró (internal+accounts → 422 már a
 * Zod-on), a delivery a küldés-mód (send MOST vagy schedule — a `scheduledAt` jövőbelisége a BFF-en, 422).
 */
export const MessageComposeBodySchema = z
    .intersection(MessageContentSchema, z.union([
    z.object({ type: z.literal('internal'), audience: InternalAudienceSchema }),
    z.object({ type: z.literal('external'), audience: ExternalAudienceSchema }),
]))
    .and(z.object({
    delivery: z.discriminatedUnion('mode', [
        z.object({ mode: z.literal('send') }),
        z.object({ mode: z.literal('schedule'), scheduledAt: z.string().datetime() }),
    ]),
}));
/** Időzítés / átidőzítés body — a `scheduledAt` UTC ISO (a UI Budapest → UTC); jövőbeliség a BFF-en (422). */
export const MessageScheduleBodySchema = z.object({
    scheduledAt: z.string().datetime(),
});
/** Üzenet-címzett-picker opció-listák (mind `[{id, name}]`, névre rendezve) — a compose-form multiselectjeihez. */
export const MessageRefsSchema = z.object({
    /** SZEMÉLY-opciók: a név-részek is jönnek, mert a névsorrend és az ABC-rend nyelv-függő (lásd `PersonOptionSchema`). */
    employees: z.array(PersonOptionSchema),
    departments: z.array(z.object({ id: z.string(), name: z.string() })),
    /** A csapat-opciók a részlegük nevével — a picker így csoportosítva tud listázni. */
    teams: z.array(z.object({ id: z.string(), name: z.string(), departmentName: z.string().nullable() })),
    accounts: z.array(z.object({ id: z.string(), name: z.string() })),
    contacts: z.array(PersonOptionSchema),
    services: z.array(z.object({ id: z.string(), name: z.string() })),
});
/**
 * Üzenet-részletnézet (a lista NEM hordoz body-t → a megnyitáshoz kell): a SZANITÁLT HTML-törzs + meta +
 * `readByMe` (az aktuális identity read-sora). A `version` az OCC If-Match forrása a sor-akciókhoz.
 */
export const MessageDetailSchema = z.object({
    id: z.string(),
    type: z.string(),
    status: z.string(),
    subject: z.string(),
    /** SZANITÁLT HTML (TipTap-allowlist) — a detail-render CSAK ezt kapja (defense-in-depth az ETL-adatra is). */
    bodyHtml: z.string(),
    googleDriveLink: z.string().nullable(),
    googleMeetLink: z.string().nullable(),
    zoomLink: z.string().nullable(),
    audienceEveryone: z.boolean(),
    audienceAllClients: z.boolean(),
    senderId: z.string().nullable(),
    sentAt: z.string().nullable(),
    scheduledAt: z.string().nullable(),
    /** Utolsó utólagos (sent-)szerkesztés — a „szerkesztve" jelzéshez; revízió-UI NINCS (write-only történet). */
    editedAt: z.string().nullable(),
    createdAt: z.string().nullable(),
    version: z.number(),
    readByMe: z.boolean(),
});
//# sourceMappingURL=schemas.js.map